import { useState, useRef, useEffect, useMemo, useCallback } from 'react';
import { Building2, Plus, Loader, MapPin, Store, ChevronRight, Eye, MoreVertical, Edit2, Trash2, Power, PowerOff, Phone, Mail, Calendar, Clock, User, Search, X } from 'lucide-react';
import { useUsers, type UserWithDetails } from '../../hooks/useUsers';
import { Card } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Modal } from '../../components/ui/Modal';
import { FormField, Input, Select } from '../../components/ui/Form';
import { OrganizationSummary } from '../../components/organization/OrganizationSummary';
import { useOrganization } from '../../hooks/useOrganization';
import { useBusinessUnit } from '../../hooks/useBusinessUnit';
import { useAuthStore } from '../../stores/authStore';
import { toast } from 'react-hot-toast';
import type { Branch, PointOfSale, BusinessUnitStatus } from '../../types/business-unit';
import { BUSINESS_UNIT_STATUS_LABELS } from '../../types/business-unit';
import type { Organization } from '../../types/organization';
import { 
  RDC_PROVINCE_NAMES, 
  getCitiesByProvince, 
  OTHER_OPTION 
} from '../../config/rdc-geography';

type TabType = 'company' | 'branches' | 'pos';

export function OrganizationPage() {
  const { organization, loading } = useOrganization();
  const { organization: authOrganization } = useAuthStore();
  const { 
    hierarchy, 
    loading: hierarchyLoading,
    loadHierarchy,
    createBusinessUnit,
    updateBusinessUnit,
    deactivateBusinessUnit,
    deleteBusinessUnit
  } = useBusinessUnit();
  
  const [activeTab, setActiveTab] = useState<TabType>('company');
  const [showBranchModal, setShowBranchModal] = useState(false);
  const [showPosModal, setShowPosModal] = useState(false);
  const [editingBranch, setEditingBranch] = useState<Branch | null>(null);
  const [editingPos, setEditingPos] = useState<PointOfSale | null>(null);
  
  // États pour les formulaires
  const [branchForm, setBranchForm] = useState({ 
    name: '', 
    city: '', 
    province: '',
    address: '', 
    phone: '', 
    email: '',
    manager: '' 
  });
  // États pour le mode "Autre" (saisie manuelle)
  const [provinceOther, setProvinceOther] = useState(false);
  const [cityOther, setCityOther] = useState(false);
  
  const [posForm, setPosForm] = useState({ 
    name: '', 
    branchId: '', 
    address: '', 
    phone: '', 
    email: '',
    posType: 'shop' as PointOfSale['posType'],
    manager: ''
  });
  const [formLoading, setFormLoading] = useState(false);
  
  // États pour les menus dropdown
  const [openBranchMenu, setOpenBranchMenu] = useState<string | null>(null);
  const [openPosMenu, setOpenPosMenu] = useState<string | null>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  // Hook pour charger les utilisateurs (pour l'autocomplétion du responsable)
  const { users: organizationUsers } = useUsers();

  // États pour l'autocomplétion du responsable
  const [showBranchManagerSuggestions, setShowBranchManagerSuggestions] = useState(false);
  const [showPosManagerSuggestions, setShowPosManagerSuggestions] = useState(false);
  const branchManagerInputRef = useRef<HTMLInputElement>(null);
  const posManagerInputRef = useRef<HTMLInputElement>(null);
  const branchSuggestionsRef = useRef<HTMLDivElement>(null);
  const posSuggestionsRef = useRef<HTMLDivElement>(null);

  // Filtrer les utilisateurs selon la saisie
  const filterUsersByName = useCallback((searchTerm: string): UserWithDetails[] => {
    if (!searchTerm.trim() || searchTerm.length < 2) return [];
    const term = searchTerm.toLowerCase();
    return organizationUsers.filter(user => {
      const fullName = `${user.firstName || ''} ${user.lastName || ''}`.toLowerCase();
      const displayName = (user.name || '').toLowerCase();
      return fullName.includes(term) || displayName.includes(term) || (user.email || '').toLowerCase().includes(term);
    }).slice(0, 5); // Limiter à 5 suggestions
  }, [organizationUsers]);

  // Suggestions filtrées pour branch
  const branchManagerSuggestions = useMemo(() => 
    filterUsersByName(branchForm.manager), 
    [branchForm.manager, filterUsersByName]
  );

  // Suggestions filtrées pour POS
  const posManagerSuggestions = useMemo(() => 
    filterUsersByName(posForm.manager), 
    [posForm.manager, filterUsersByName]
  );

  // Sélectionner un manager pour branch et auto-remplir les champs
  const selectBranchManager = useCallback((user: UserWithDetails) => {
    const managerName = user.name || `${user.firstName || ''} ${user.lastName || ''}`.trim();
    setBranchForm(prev => ({
      ...prev,
      manager: managerName,
      phone: user.phone || prev.phone, // Ne remplacer que si l'user a un téléphone
      email: user.email || prev.email  // Ne remplacer que si l'user a un email
    }));
    setShowBranchManagerSuggestions(false);
  }, []);

  // Sélectionner un manager pour POS et auto-remplir les champs
  const selectPosManager = useCallback((user: UserWithDetails) => {
    const managerName = user.name || `${user.firstName || ''} ${user.lastName || ''}`.trim();
    setPosForm(prev => ({
      ...prev,
      manager: managerName,
      phone: user.phone || prev.phone,
      email: user.email || prev.email
    }));
    setShowPosManagerSuggestions(false);
  }, []);

  // Fermer les suggestions quand on clique ailleurs
  useEffect(() => {
    function handleClickOutsideSuggestions(event: MouseEvent) {
      if (
        branchSuggestionsRef.current && 
        !branchSuggestionsRef.current.contains(event.target as Node) &&
        branchManagerInputRef.current &&
        !branchManagerInputRef.current.contains(event.target as Node)
      ) {
        setShowBranchManagerSuggestions(false);
      }
      if (
        posSuggestionsRef.current && 
        !posSuggestionsRef.current.contains(event.target as Node) &&
        posManagerInputRef.current &&
        !posManagerInputRef.current.contains(event.target as Node)
      ) {
        setShowPosManagerSuggestions(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutsideSuggestions);
    return () => document.removeEventListener('mousedown', handleClickOutsideSuggestions);
  }, []);

  // Charger la hiérarchie au montage de la page (forcer le rechargement depuis l'API)
  useEffect(() => {
    loadHierarchy();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Exécuter une seule fois au montage de la page

  // Fermer les menus quand on clique ailleurs
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setOpenBranchMenu(null);
        setOpenPosMenu(null);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Créer un objet Organization à partir de hierarchy.company si organization n'existe pas
  // Cela permet d'afficher les onglets même en mode développement sans backend
  const organizationData: Organization | null = useMemo(() => {
    if (organization) {
      return organization;
    }
    
    // Fallback: créer un objet Organization à partir de hierarchy.company
    if (hierarchy?.company) {
      return {
        id: hierarchy.company.id,
        name: hierarchy.company.name,
        registrationNumber: '',
        taxId: '',
        vatNumber: null,
        cnssNumber: null,
        inppNumber: null,
        address: hierarchy.company.address || '',
        city: hierarchy.company.city || null,
        country: hierarchy.company.country || 'RDC',
        phone: hierarchy.company.phone || '',
        email: hierarchy.company.email || '',
        website: null,
        legalForm: '',
        capital: '',
        currency: hierarchy.company.currency || 'CDF',
        accountingMode: undefined,
        logo: null,
        industry: ''
      };
    }
    
    return null;
  }, [organization, hierarchy]);

  // companyId = organization.id de authStore (source de vérité) ou fallback sur hierarchy
  const effectiveCompanyId = useMemo(() => {
    return authOrganization?.id || hierarchy?.company?.id || '';
  }, [authOrganization?.id, hierarchy?.company?.id]);

  // Villes disponibles selon la province sélectionnée
  const availableCities = useMemo(() => {
    if (!branchForm.province || provinceOther) return [];
    return getCitiesByProvince(branchForm.province);
  }, [branchForm.province, provinceOther]);

  if (loading || hierarchyLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader className="h-8 w-8 text-primary animate-spin" />
        <span className="ml-2 text-foreground">Chargement des informations de l'entreprise...</span>
      </div>
    );
  }

  // Afficher l'état vide seulement si ni organization ni hierarchy n'existent
  // L'entreprise doit être configurée via l'application de gestion des organisations
  if (!organization && !hierarchy) {
    return (
      <div className="space-y-6">
        <div className="flex justify-between items-center">
          <h1 className="text-2xl font-bold text-foreground">Organisation</h1>
        </div>

        <Card>
          <div className="text-center py-12">
            <Building2 className="mx-auto h-12 w-12 text-gray-400 mb-4" />
            <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
              Aucune entreprise configurée
            </h3>
            <p className="text-gray-500 dark:text-gray-400 mb-6">
              Votre entreprise n'est pas encore configurée. Veuillez contacter votre administrateur 
              ou configurer votre organisation via l'application de gestion.
            </p>
          </div>
        </Card>
      </div>
    );
  }

  // Créer une succursale
  const handleCreateBranch = async () => {
    if (!branchForm.name.trim()) {
      toast.error('Le nom de la succursale est requis');
      return;
    }
    if (!effectiveCompanyId) {
      toast.error('Organisation non configurée. Veuillez vous reconnecter.');
      return;
    }
    
    setFormLoading(true);
    try {
      if (editingBranch) {
        // Mise à jour d'une succursale existante
        const result = await updateBusinessUnit(editingBranch.id, {
          name: branchForm.name,
          city: branchForm.city,
          province: branchForm.province,
          address: branchForm.address,
          phone: branchForm.phone,
          email: branchForm.email,
          manager: branchForm.manager
        });
        
        if (result) {
          toast.success(`Succursale "${branchForm.name}" mise à jour`);
        } else {
          toast.error('Erreur lors de la mise à jour');
        }
      } else {
        // Création d'une nouvelle succursale via l'API
        const result = await createBusinessUnit({
          code: `BRN-${Date.now().toString(36).toUpperCase()}`, // Code temporaire, le backend générera le vrai
          name: branchForm.name,
          type: 'branch',
          companyId: effectiveCompanyId,
          parentId: effectiveCompanyId,
          city: branchForm.city,
          province: branchForm.province,
          address: branchForm.address,
          phone: branchForm.phone,
          email: branchForm.email,
          manager: branchForm.manager,
          country: 'RDC',
          currency: 'CDF',
          timezone: 'Africa/Kinshasa'
        });
        
        if (result) {
          toast.success(`Succursale "${branchForm.name}" créée avec le code ${result.code}`);
        } else {
          toast.error('Erreur lors de la création');
        }
      }
      
      setShowBranchModal(false);
      setEditingBranch(null);
      setBranchForm({ name: '', city: '', province: '', address: '', phone: '', email: '', manager: '' });
      setProvinceOther(false);
      setCityOther(false);
    } catch (error) {
      console.error('Erreur lors de la création/mise à jour de la succursale:', error);
      toast.error('Erreur lors de l\'opération');
    } finally {
      setFormLoading(false);
    }
  };

  // Créer un point de vente
  const handleCreatePos = async () => {
    if (!posForm.name.trim()) {
      toast.error('Le nom du point de vente est requis');
      return;
    }
    if (!effectiveCompanyId) {
      toast.error('Organisation non configurée. Veuillez vous reconnecter.');
      return;
    }
    // branchId est maintenant optionnel - un POS peut être directement sous l'entreprise
    
    setFormLoading(true);
    try {
      // parentId: si une succursale est sélectionnée, utiliser son ID, sinon utiliser le companyId
      const parentId = posForm.branchId || effectiveCompanyId;
      
      if (editingPos) {
        // Mise à jour d'un POS existant
        const result = await updateBusinessUnit(editingPos.id, {
          name: posForm.name,
          address: posForm.address,
          phone: posForm.phone,
          email: posForm.email,
          manager: posForm.manager
        });
        
        if (result) {
          toast.success(`Point de vente "${posForm.name}" mis à jour`);
        } else {
          toast.error('Erreur lors de la mise à jour');
        }
      } else {
        // Création d'un nouveau POS via l'API
        const result = await createBusinessUnit({
          code: `POS-${Date.now().toString(36).toUpperCase()}`, // Code temporaire
          name: posForm.name,
          type: 'pos',
          companyId: effectiveCompanyId,
          parentId,
          address: posForm.address,
          phone: posForm.phone,
          email: posForm.email,
          manager: posForm.manager,
          country: 'RDC',
          currency: 'CDF',
          timezone: 'Africa/Kinshasa'
        });
        
        if (result) {
          toast.success(`Point de vente "${posForm.name}" créé avec le code ${result.code}`);
        } else {
          toast.error('Erreur lors de la création');
        }
      }
      
      setShowPosModal(false);
      setEditingPos(null);
      setPosForm({ name: '', branchId: '', address: '', phone: '', email: '', posType: 'shop', manager: '' });
    } catch (error) {
      console.error('Erreur lors de la création/mise à jour du point de vente:', error);
      toast.error('Erreur lors de l\'opération');
    } finally {
      setFormLoading(false);
    }
  };

  // Fonction helper pour obtenir le style du badge de statut
  const getStatusBadgeStyle = (status: BusinessUnitStatus) => {
    switch (status) {
      case 'active':
        return 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400';
      case 'inactive':
        return 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-400';
      case 'suspended':
        return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400';
      case 'closed':
        return 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400';
      default:
        return 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-400';
    }
  };

  // Handlers pour désactiver/supprimer
  const handleDeactivateUnit = async (id: string, name: string) => {
    if (window.confirm(`Désactiver "${name}" et toutes ses sous-unités ?`)) {
      const result = await deactivateBusinessUnit(id);
      if (result) {
        toast.success(`"${name}" a été désactivé`);
      } else {
        toast.error('Erreur lors de la désactivation');
      }
    }
  };

  const handleDeleteUnit = async (id: string, name: string, type: 'branch' | 'pos') => {
    const typeLabel = type === 'branch' ? 'la succursale' : 'le point de vente';
    if (window.confirm(`Supprimer définitivement ${typeLabel} "${name}" ? Cette action est irréversible.`)) {
      const result = await deleteBusinessUnit(id);
      if (result) {
        toast.success(`${typeLabel === 'la succursale' ? 'Succursale' : 'Point de vente'} supprimé(e)`);
      } else {
        toast.error('Erreur lors de la suppression');
      }
    }
  };

  const tabs = [
    { id: 'company' as TabType, label: 'Entreprise Générale', icon: Building2 },
    { id: 'branches' as TabType, label: 'Succursales', icon: MapPin, count: hierarchy?.branches?.length || 0 },
    { id: 'pos' as TabType, label: 'Points de vente', icon: Store, count: hierarchy?.pointsOfSale?.length || 0 }
  ];

  // Composant réutilisable pour afficher un point de vente
  const PosCard = ({ pos }: { pos: PointOfSale }) => {
    const status = pos.status || (pos.isActive ? 'active' : 'inactive');
    
    return (
    <Card className="hover:shadow-md transition-shadow">
      <div className="p-4">
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-purple-100 dark:bg-purple-900/30 rounded-lg">
              <Store className="h-5 w-5 text-purple-600 dark:text-purple-400" />
            </div>
            <div>
              <h4 className="font-semibold text-foreground">{pos.name}</h4>
              <p className="text-xs text-muted-foreground">Code: {pos.code}</p>
            </div>
          </div>
          <div className="relative" ref={openPosMenu === pos.id ? menuRef : null}>
            <button
              onClick={() => setOpenPosMenu(openPosMenu === pos.id ? null : pos.id)}
              className="p-1.5 text-muted-foreground hover:text-foreground hover:bg-muted rounded"
            >
              <MoreVertical className="h-4 w-4" />
            </button>
            {openPosMenu === pos.id && (
              <div className="absolute right-0 top-full mt-1 w-48 bg-card border border-border rounded-lg shadow-lg z-10">
                <button
                  onClick={() => {
                    setEditingPos(pos);
                    setPosForm({
                      name: pos.name,
                      branchId: pos.branchId || '',
                      address: pos.address || '',
                      phone: pos.phone || '',
                      email: pos.email || '',
                      posType: pos.posType || 'shop',
                      manager: pos.manager || ''
                    });
                    setShowPosModal(true);
                    setOpenPosMenu(null);
                  }}
                  className="w-full flex items-center gap-2 px-3 py-2 text-sm text-foreground hover:bg-muted transition-colors"
                >
                  <Edit2 className="h-4 w-4" />
                  Modifier
                </button>
                <button
                  onClick={() => {
                    handleDeactivateUnit(pos.id, pos.name);
                    setOpenPosMenu(null);
                  }}
                  className="w-full flex items-center gap-2 px-3 py-2 text-sm text-yellow-600 hover:bg-yellow-50 dark:hover:bg-yellow-900/20 transition-colors"
                >
                  <PowerOff className="h-4 w-4" />
                  Désactiver
                </button>
                <button
                  onClick={() => {
                    handleDeleteUnit(pos.id, pos.name, 'pos');
                    setOpenPosMenu(null);
                  }}
                  className="w-full flex items-center gap-2 px-3 py-2 text-sm text-destructive hover:bg-destructive/10 transition-colors"
                >
                  <Trash2 className="h-4 w-4" />
                  Supprimer
                </button>
              </div>
            )}
          </div>
        </div>
        
        <div className="mt-3 space-y-2 text-sm">
          {pos.posType && (
            <div className="flex items-center gap-2 text-muted-foreground">
              <Store className="h-3.5 w-3.5 text-purple-500" />
              <span>{
                { shop: 'Magasin', warehouse: 'Entrepôt', kiosk: 'Kiosque', online: 'En ligne', other: 'Autre' }[pos.posType]
              }</span>
            </div>
          )}
          {pos.manager && (
            <div className="flex items-center gap-2 text-muted-foreground">
              <User className="h-3.5 w-3.5 text-blue-500" />
              <span>{pos.manager}</span>
            </div>
          )}
          {pos.phone && (
            <div className="flex items-center gap-2 text-muted-foreground">
              <Phone className="h-3.5 w-3.5 text-green-500" />
              <a href={`tel:${pos.phone}`} className="hover:text-primary hover:underline">{pos.phone}</a>
            </div>
          )}
          {pos.email && (
            <div className="flex items-center gap-2 text-muted-foreground">
              <Mail className="h-3.5 w-3.5 text-red-500" />
              <a href={`mailto:${pos.email}`} className="hover:text-primary hover:underline truncate">{pos.email}</a>
            </div>
          )}
          {pos.address && (
            <div className="flex items-center gap-2 text-muted-foreground">
              <MapPin className="h-3.5 w-3.5 text-orange-500" />
              <span className="truncate" title={pos.address}>{pos.address}</span>
            </div>
          )}
          {pos.createdAt && (
            <div className="flex items-center gap-2 text-xs text-muted-foreground/70 pt-1">
              <Calendar className="h-3 w-3" />
              <span>Créé le {new Date(pos.createdAt).toLocaleDateString('fr-FR')}</span>
            </div>
          )}
        </div>
        
        <div className="mt-3 pt-3 border-t border-border">
          <span className={`
            inline-flex items-center px-2 py-1 rounded-full text-xs font-medium
            ${getStatusBadgeStyle(status)}
          `}>
            {BUSINESS_UNIT_STATUS_LABELS[status] || status}
          </span>
        </div>
      </div>
    </Card>
  );
  };

  return (
    <div className="space-y-6">
      {/* En-tête */}
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-bold text-foreground">Organisation</h1>
      </div>

      {/* Tabs de navigation */}
      <div className="border-b border-border">
        <nav className="-mb-px flex space-x-8">
          {tabs.map((tab) => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`
                  flex items-center gap-2 py-4 px-1 border-b-2 font-medium text-sm
                  transition-colors duration-200
                  ${activeTab === tab.id 
                    ? 'border-primary text-primary' 
                    : 'border-transparent text-muted-foreground hover:text-foreground hover:border-border'}
                `}
              >
                <Icon className="h-4 w-4" />
                {tab.label}
                {tab.count !== undefined && (
                  <span className={`
                    px-2 py-0.5 text-xs rounded-full
                    ${activeTab === tab.id 
                      ? 'bg-primary/10 text-primary' 
                      : 'bg-muted text-muted-foreground'}
                  `}>
                    {tab.count}
                  </span>
                )}
              </button>
            );
          })}
        </nav>
      </div>

      {/* Contenu des tabs */}
      {activeTab === 'company' && (
        <div className="space-y-6">
          {/* Bandeau informatif - lecture seule */}
          <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
            <div className="flex items-center gap-3">
              <Eye className="h-5 w-5 text-blue-600 flex-shrink-0" />
              <div>
                <p className="text-sm font-medium text-blue-900 dark:text-blue-100">
                  Informations de l'entreprise générale
                </p>
                <p className="text-xs text-blue-700 dark:text-blue-300">
                  Ces informations sont en lecture seule. Contactez l'administrateur pour les modifier.
                </p>
              </div>
            </div>
          </div>

          {/* Informations de l'entreprise - Layout complet avec logo intégré */}
          {organizationData && <OrganizationSummary organization={organizationData} />}
        </div>
      )}

      {activeTab === 'branches' && (
        <div className="space-y-6">
          {/* Actions */}
          <div className="flex justify-end">
            <Button
              variant="primary"
              icon={Plus}
              onClick={() => setShowBranchModal(true)}
            >
              Nouvelle Succursale
            </Button>
          </div>

          {/* Liste des succursales */}
          {hierarchy?.branches && hierarchy.branches.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {hierarchy.branches.map((branch) => {
                const branchPosCount = hierarchy.pointsOfSale.filter(
                  pos => pos.branchId === branch.id
                ).length;
                
                return (
                  <Card key={branch.id} className="hover:shadow-md transition-shadow">
                    <div className="p-4">
                      <div className="flex items-start justify-between">
                        <div className="flex items-center gap-3">
                          <div className="p-2 bg-green-100 dark:bg-green-900/30 rounded-lg">
                            <MapPin className="h-5 w-5 text-green-600 dark:text-green-400" />
                          </div>
                          <div>
                            <h3 className="font-semibold text-foreground">{branch.name}</h3>
                            <p className="text-xs text-muted-foreground">Code: {branch.code}</p>
                          </div>
                        </div>
                        <div className="relative" ref={openBranchMenu === branch.id ? menuRef : null}>
                          <button
                            onClick={() => setOpenBranchMenu(openBranchMenu === branch.id ? null : branch.id)}
                            className="p-1.5 text-muted-foreground hover:text-foreground hover:bg-muted rounded"
                          >
                            <MoreVertical className="h-4 w-4" />
                          </button>
                          {openBranchMenu === branch.id && (
                            <div className="absolute right-0 top-full mt-1 w-48 bg-card border border-border rounded-lg shadow-lg z-10">
                              <button
                                onClick={() => {
                                  setEditingBranch(branch);
                                  setBranchForm({
                                    name: branch.name,
                                    city: branch.city || '',
                                    province: branch.province || '',
                                    address: branch.address || '',
                                    phone: branch.phone || '',
                                    email: branch.email || '',
                                    manager: branch.manager || ''
                                  });
                                  setShowBranchModal(true);
                                  setOpenBranchMenu(null);
                                }}
                                className="w-full flex items-center gap-2 px-3 py-2 text-sm text-foreground hover:bg-muted transition-colors"
                              >
                                <Edit2 className="h-4 w-4" />
                                Modifier
                              </button>
                              <button
                                onClick={() => {
                                  handleDeactivateUnit(branch.id, branch.name);
                                  setOpenBranchMenu(null);
                                }}
                                className="w-full flex items-center gap-2 px-3 py-2 text-sm text-yellow-600 hover:bg-yellow-50 dark:hover:bg-yellow-900/20 transition-colors"
                              >
                                <PowerOff className="h-4 w-4" />
                                Désactiver
                              </button>
                              <button
                                onClick={() => {
                                  handleDeleteUnit(branch.id, branch.name, 'branch');
                                  setOpenBranchMenu(null);
                                }}
                                className="w-full flex items-center gap-2 px-3 py-2 text-sm text-destructive hover:bg-destructive/10 transition-colors"
                              >
                                <Trash2 className="h-4 w-4" />
                                Supprimer
                              </button>
                            </div>
                          )}
                        </div>
                      </div>
                      
                      <div className="mt-4 space-y-2 text-sm">
                        {branch.city && (
                          <div className="flex items-center gap-2 text-muted-foreground">
                            <MapPin className="h-3.5 w-3.5 text-orange-500" />
                            <span>{branch.city}{branch.province && `, ${branch.province}`}</span>
                          </div>
                        )}
                        {branch.manager && (
                          <div className="flex items-center gap-2 text-muted-foreground">
                            <User className="h-3.5 w-3.5 text-blue-500" />
                            <span>{branch.manager}</span>
                          </div>
                        )}
                        {branch.phone && (
                          <div className="flex items-center gap-2 text-muted-foreground">
                            <Phone className="h-3.5 w-3.5 text-green-500" />
                            <a href={`tel:${branch.phone}`} className="hover:text-primary hover:underline">{branch.phone}</a>
                          </div>
                        )}
                        {branch.email && (
                          <div className="flex items-center gap-2 text-muted-foreground">
                            <Mail className="h-3.5 w-3.5 text-red-500" />
                            <a href={`mailto:${branch.email}`} className="hover:text-primary hover:underline truncate">{branch.email}</a>
                          </div>
                        )}
                        {branch.address && (
                          <div className="flex items-center gap-2 text-muted-foreground">
                            <Building2 className="h-3.5 w-3.5 text-gray-500" />
                            <span className="truncate" title={branch.address}>{branch.address}</span>
                          </div>
                        )}
                        <div className="flex items-center justify-between gap-4 pt-2 border-t border-border mt-2">
                          <div className="flex items-center gap-2">
                            <Store className="h-4 w-4 text-purple-500" />
                            <span className="text-muted-foreground">
                              {branchPosCount} point{branchPosCount > 1 ? 's' : ''} de vente
                            </span>
                          </div>
                          {branch.createdAt && (
                            <div className="flex items-center gap-1 text-xs text-muted-foreground/70">
                              <Calendar className="h-3 w-3" />
                              <span>{new Date(branch.createdAt).toLocaleDateString('fr-FR')}</span>
                            </div>
                          )}
                        </div>
                      </div>
                      
                      <div className="mt-3 pt-3 border-t border-border">
                        {(() => {
                          const branchStatus = branch.status || (branch.isActive ? 'active' : 'inactive');
                          return (
                            <span className={`
                              inline-flex items-center px-2 py-1 rounded-full text-xs font-medium
                              ${getStatusBadgeStyle(branchStatus)}
                            `}>
                              {BUSINESS_UNIT_STATUS_LABELS[branchStatus] || branchStatus}
                            </span>
                          );
                        })()}
                      </div>
                    </div>
                  </Card>
                );
              })}
            </div>
          ) : (
            <Card>
              <div className="text-center py-12">
                <MapPin className="mx-auto h-12 w-12 text-gray-400 mb-4" />
                <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
                  Aucune succursale
                </h3>
                <p className="text-gray-500 dark:text-gray-400 mb-6">
                  Créez des succursales pour organiser votre entreprise par zone géographique.
                </p>
                <Button
                  variant="primary"
                  icon={Plus}
                  onClick={() => setShowBranchModal(true)}
                >
                  Créer une succursale
                </Button>
              </div>
            </Card>
          )}
        </div>
      )}

      {activeTab === 'pos' && (
        <div className="space-y-6">
          {/* Actions */}
          <div className="flex justify-end">
            <Button
              variant="primary"
              icon={Plus}
              onClick={() => setShowPosModal(true)}
            >
              Nouveau Point de vente
            </Button>
          </div>

          {/* Information - les succursales sont optionnelles */}
          <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
            <div className="flex items-center gap-3">
              <Building2 className="h-5 w-5 text-blue-600" />
              <p className="text-sm text-blue-800 dark:text-blue-200">
                Les points de vente peuvent être rattachés à une succursale ou directement à l'entreprise générale.
              </p>
            </div>
          </div>

          {/* Points de vente directement sous l'entreprise (sans succursale) */}
          {(() => {
            const directPos = hierarchy?.pointsOfSale?.filter(pos => !pos.branchId) || [];
            if (directPos.length === 0) return null;
            
            return (
              <div>
                <div className="flex items-center gap-2 mb-3">
                  <Building2 className="h-4 w-4 text-primary" />
                  <h3 className="font-medium text-foreground">Entreprise Générale</h3>
                  <ChevronRight className="h-4 w-4 text-muted-foreground" />
                  <span className="text-sm text-muted-foreground">
                    {directPos.length} point{directPos.length > 1 ? 's' : ''} de vente
                  </span>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 ml-6">
                  {directPos.map((pos) => (
                    <PosCard key={pos.id} pos={pos} />
                  ))}
                </div>
              </div>
            );
          })()}

          {/* Liste des points de vente groupés par succursale */}
          {hierarchy?.branches && hierarchy.branches.length > 0 && (
            <div className="space-y-6">
              {hierarchy.branches.map((branch) => {
                const branchPos = hierarchy.pointsOfSale.filter(
                  pos => pos.branchId === branch.id
                );
                
                return (
                  <div key={branch.id}>
                    <div className="flex items-center gap-2 mb-3">
                      <MapPin className="h-4 w-4 text-green-600" />
                      <h3 className="font-medium text-foreground">{branch.name}</h3>
                      <ChevronRight className="h-4 w-4 text-muted-foreground" />
                      <span className="text-sm text-muted-foreground">
                        {branchPos.length} point{branchPos.length > 1 ? 's' : ''} de vente
                      </span>
                    </div>
                    
                    {branchPos.length > 0 ? (
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 ml-6">
                        {branchPos.map((pos) => (
                          <PosCard key={pos.id} pos={pos} />
                        ))}
                      </div>
                    ) : (
                      <div className="ml-6 p-4 bg-muted/50 rounded-lg text-center">
                        <p className="text-sm text-muted-foreground">
                          Aucun point de vente dans cette succursale
                        </p>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}

          {/* État vide global */}
          {(!hierarchy?.pointsOfSale || hierarchy.pointsOfSale.length === 0) && (
            <Card>
              <div className="text-center py-12">
                <Store className="mx-auto h-12 w-12 text-gray-400 mb-4" />
                <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
                  Aucun point de vente
                </h3>
                <p className="text-gray-500 dark:text-gray-400 mb-6">
                  Créez des points de vente pour suivre les transactions par emplacement.
                </p>
                <Button
                  variant="primary"
                  icon={Plus}
                  onClick={() => setShowPosModal(true)}
                >
                  Créer un point de vente
                </Button>
              </div>
            </Card>
          )}
        </div>
      )}

{/* Modal de création/modification de succursale */}
      <Modal
        isOpen={showBranchModal}
        onClose={() => {
          setShowBranchModal(false);
          setEditingBranch(null);
          setBranchForm({ name: '', city: '', province: '', address: '', phone: '', email: '', manager: '' });
          setProvinceOther(false);
          setCityOther(false);
        }}
        title={editingBranch ? 'Modifier la succursale' : 'Nouvelle succursale'}
      >
        <div className="space-y-4">
          <FormField label="Nom de la succursale" required>
            <Input
              value={branchForm.name}
              onChange={(e) => setBranchForm(prev => ({ ...prev, name: e.target.value }))}
              placeholder="Ex: Succursale Nord"
            />
          </FormField>

          <FormField label="Responsable" hint="Tapez pour rechercher parmi les utilisateurs">
            <div className="relative">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
                <input
                  ref={branchManagerInputRef}
                  type="text"
                  value={branchForm.manager}
                  onChange={(e) => {
                    setBranchForm(prev => ({ ...prev, manager: e.target.value }));
                    setShowBranchManagerSuggestions(e.target.value.length >= 2);
                  }}
                  onFocus={() => branchForm.manager.length >= 2 && setShowBranchManagerSuggestions(true)}
                  placeholder="Ex: Jean Dupont"
                  className="w-full pl-10 pr-8 py-2 border border-border rounded-lg bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
                  autoComplete="off"
                />
                {branchForm.manager && (
                  <button
                    type="button"
                    onClick={() => {
                      setBranchForm(prev => ({ ...prev, manager: '' }));
                      setShowBranchManagerSuggestions(false);
                    }}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-muted-foreground hover:text-foreground"
                  >
                    <X className="h-4 w-4" />
                  </button>
                )}
              </div>
              {/* Liste des suggestions */}
              {showBranchManagerSuggestions && branchManagerSuggestions.length > 0 && (
                <div
                  ref={branchSuggestionsRef}
                  className="absolute z-50 w-full mt-1 bg-card border border-border rounded-lg shadow-lg max-h-60 overflow-auto"
                >
                  {branchManagerSuggestions.map((user) => (
                    <button
                      key={user.id}
                      type="button"
                      onClick={() => selectBranchManager(user)}
                      className="w-full px-4 py-3 text-left hover:bg-muted/50 flex items-center gap-3 border-b border-border last:border-b-0 transition-colors"
                    >
                      <div className="flex-shrink-0 w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
                        <User className="h-4 w-4 text-primary" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-foreground truncate">
                          {user.name || `${user.firstName || ''} ${user.lastName || ''}`.trim() || 'Utilisateur'}
                        </p>
                        <div className="flex items-center gap-3 text-xs text-muted-foreground">
                          {user.email && (
                            <span className="flex items-center gap-1 truncate">
                              <Mail className="h-3 w-3" />
                              {user.email}
                            </span>
                          )}
                          {user.phone && (
                            <span className="flex items-center gap-1">
                              <Phone className="h-3 w-3" />
                              {user.phone}
                            </span>
                          )}
                        </div>
                      </div>
                    </button>
                  ))}
                </div>
              )}
              {showBranchManagerSuggestions && branchForm.manager.length >= 2 && branchManagerSuggestions.length === 0 && (
                <div className="absolute z-50 w-full mt-1 bg-card border border-border rounded-lg shadow-lg p-3 text-sm text-muted-foreground">
                  Aucun utilisateur trouvé. Vous pouvez saisir le nom manuellement.
                </div>
              )}
            </div>
          </FormField>
          
          <div className="grid grid-cols-2 gap-4">
            <FormField label="Province">
              {provinceOther ? (
                <div className="flex gap-2">
                  <Input
                    value={branchForm.province}
                    onChange={(e) => setBranchForm(prev => ({ ...prev, province: e.target.value }))}
                    placeholder="Saisir la province"
                  />
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      setProvinceOther(false);
                      setBranchForm(prev => ({ ...prev, province: '', city: '' }));
                      setCityOther(false);
                    }}
                    title="Revenir à la liste"
                  >
                    ↩
                  </Button>
                </div>
              ) : (
                <Select
                  value={branchForm.province}
                  onChange={(e) => {
                    const value = e.target.value;
                    if (value === OTHER_OPTION) {
                      setProvinceOther(true);
                      setBranchForm(prev => ({ ...prev, province: '', city: '' }));
                    } else {
                      setBranchForm(prev => ({ ...prev, province: value, city: '' }));
                      setCityOther(false);
                    }
                  }}
                >
                  <option value="">Sélectionner une province</option>
                  {RDC_PROVINCE_NAMES.map(prov => (
                    <option key={prov} value={prov}>{prov}</option>
                  ))}
                  <option value={OTHER_OPTION}>Autre (saisie libre)</option>
                </Select>
              )}
            </FormField>

            <FormField label="Ville">
              {provinceOther || cityOther ? (
                <div className="flex gap-2">
                  <Input
                    value={branchForm.city}
                    onChange={(e) => setBranchForm(prev => ({ ...prev, city: e.target.value }))}
                    placeholder="Saisir la ville"
                  />
                  {!provinceOther && cityOther && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        setCityOther(false);
                        setBranchForm(prev => ({ ...prev, city: '' }));
                      }}
                      title="Revenir à la liste"
                    >
                      ↩
                    </Button>
                  )}
                </div>
              ) : (
                <Select
                  value={branchForm.city}
                  onChange={(e) => {
                    const value = e.target.value;
                    if (value === OTHER_OPTION) {
                      setCityOther(true);
                      setBranchForm(prev => ({ ...prev, city: '' }));
                    } else {
                      setBranchForm(prev => ({ ...prev, city: value }));
                    }
                  }}
                  disabled={!branchForm.province}
                >
                  <option value="">
                    {branchForm.province ? 'Sélectionner une ville' : 'Choisir d\'abord une province'}
                  </option>
                  {availableCities.map(city => (
                    <option key={city} value={city}>{city}</option>
                  ))}
                  {branchForm.province && (
                    <option value={OTHER_OPTION}>Autre (saisie libre)</option>
                  )}
                </Select>
              )}
            </FormField>
          </div>
          
          <FormField label="Adresse">
            <Input
              value={branchForm.address}
              onChange={(e) => setBranchForm(prev => ({ ...prev, address: e.target.value }))}
              placeholder="Ex: 123 Avenue du Commerce"
            />
          </FormField>
          
          <div className="grid grid-cols-2 gap-4">
            <FormField label="Téléphone">
              <Input
                value={branchForm.phone}
                onChange={(e) => setBranchForm(prev => ({ ...prev, phone: e.target.value }))}
                placeholder="Ex: +243 999 999 999"
              />
            </FormField>
            
            <FormField label="Email">
              <Input
                type="email"
                value={branchForm.email}
                onChange={(e) => setBranchForm(prev => ({ ...prev, email: e.target.value }))}
                placeholder="Ex: nord@entreprise.com"
              />
            </FormField>
          </div>
          
          <div className="flex justify-end gap-3 pt-4">
            <Button
              variant="outline"
              onClick={() => {
                setShowBranchModal(false);
                setEditingBranch(null);
                setBranchForm({ name: '', city: '', province: '', address: '', phone: '', email: '', manager: '' });
                setProvinceOther(false);
                setCityOther(false);
              }}
            >
              Annuler
            </Button>
            <Button
              variant="primary"
              onClick={handleCreateBranch}
              disabled={formLoading}
            >
              {formLoading ? (
                <>
                  <Loader className="h-4 w-4 animate-spin mr-2" />
                  {editingBranch ? 'Mise à jour...' : 'Création...'}
                </>
              ) : editingBranch ? 'Modifier' : 'Créer'}
            </Button>
          </div>
        </div>
      </Modal>

      {/* Modal de création/modification de point de vente */}
      <Modal
        isOpen={showPosModal}
        onClose={() => {
          setShowPosModal(false);
          setEditingPos(null);
          setPosForm({ name: '', branchId: '', address: '', phone: '', email: '', posType: 'shop', manager: '' });
        }}
        title={editingPos ? 'Modifier le point de vente' : 'Nouveau point de vente'}
      >
        <div className="space-y-4">
          <FormField label="Rattachement" hint="Optionnel - laissez vide pour rattacher directement à l'entreprise">
            <select
              value={posForm.branchId}
              onChange={(e) => setPosForm(prev => ({ ...prev, branchId: e.target.value }))}
              className="w-full px-3 py-2 border border-border rounded-lg bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
            >
              <option value="">Entreprise générale (sans succursale)</option>
              {hierarchy?.branches?.map(branch => (
                <option key={branch.id} value={branch.id}>
                  {branch.name}
                </option>
              ))}
            </select>
          </FormField>
          
          <FormField label="Nom du point de vente" required>
            <Input
              value={posForm.name}
              onChange={(e) => setPosForm(prev => ({ ...prev, name: e.target.value }))}
              placeholder="Ex: Magasin Centre"
            />
          </FormField>

          <FormField label="Responsable" hint="Tapez pour rechercher parmi les utilisateurs">
            <div className="relative">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
                <input
                  ref={posManagerInputRef}
                  type="text"
                  value={posForm.manager}
                  onChange={(e) => {
                    setPosForm(prev => ({ ...prev, manager: e.target.value }));
                    setShowPosManagerSuggestions(e.target.value.length >= 2);
                  }}
                  onFocus={() => posForm.manager.length >= 2 && setShowPosManagerSuggestions(true)}
                  placeholder="Ex: Marie Kabongo"
                  className="w-full pl-10 pr-8 py-2 border border-border rounded-lg bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
                  autoComplete="off"
                />
                {posForm.manager && (
                  <button
                    type="button"
                    onClick={() => {
                      setPosForm(prev => ({ ...prev, manager: '' }));
                      setShowPosManagerSuggestions(false);
                    }}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-muted-foreground hover:text-foreground"
                  >
                    <X className="h-4 w-4" />
                  </button>
                )}
              </div>
              {/* Liste des suggestions */}
              {showPosManagerSuggestions && posManagerSuggestions.length > 0 && (
                <div
                  ref={posSuggestionsRef}
                  className="absolute z-50 w-full mt-1 bg-card border border-border rounded-lg shadow-lg max-h-60 overflow-auto"
                >
                  {posManagerSuggestions.map((user) => (
                    <button
                      key={user.id}
                      type="button"
                      onClick={() => selectPosManager(user)}
                      className="w-full px-4 py-3 text-left hover:bg-muted/50 flex items-center gap-3 border-b border-border last:border-b-0 transition-colors"
                    >
                      <div className="flex-shrink-0 w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
                        <User className="h-4 w-4 text-primary" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-foreground truncate">
                          {user.name || `${user.firstName || ''} ${user.lastName || ''}`.trim() || 'Utilisateur'}
                        </p>
                        <div className="flex items-center gap-3 text-xs text-muted-foreground">
                          {user.email && (
                            <span className="flex items-center gap-1 truncate">
                              <Mail className="h-3 w-3" />
                              {user.email}
                            </span>
                          )}
                          {user.phone && (
                            <span className="flex items-center gap-1">
                              <Phone className="h-3 w-3" />
                              {user.phone}
                            </span>
                          )}
                        </div>
                      </div>
                    </button>
                  ))}
                </div>
              )}
              {showPosManagerSuggestions && posForm.manager.length >= 2 && posManagerSuggestions.length === 0 && (
                <div className="absolute z-50 w-full mt-1 bg-card border border-border rounded-lg shadow-lg p-3 text-sm text-muted-foreground">
                  Aucun utilisateur trouvé. Vous pouvez saisir le nom manuellement.
                </div>
              )}
            </div>
          </FormField>
          
          <FormField label="Type de point de vente">
            <select
              value={posForm.posType}
              onChange={(e) => setPosForm(prev => ({ ...prev, posType: e.target.value as PointOfSale['posType'] }))}
              className="w-full px-3 py-2 border border-border rounded-lg bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
            >
              <option value="shop">Magasin</option>
              <option value="warehouse">Entrepôt</option>
              <option value="kiosk">Kiosque</option>
              <option value="online">En ligne</option>
              <option value="other">Autre</option>
            </select>
          </FormField>
          
          <FormField label="Adresse">
            <Input
              value={posForm.address}
              onChange={(e) => setPosForm(prev => ({ ...prev, address: e.target.value }))}
              placeholder="Ex: 45 Rue Principale"
            />
          </FormField>
          
          <div className="grid grid-cols-2 gap-4">
            <FormField label="Téléphone">
              <Input
                value={posForm.phone}
                onChange={(e) => setPosForm(prev => ({ ...prev, phone: e.target.value }))}
                placeholder="Ex: +243 888 888 888"
              />
            </FormField>
            
            <FormField label="Email">
              <Input
                type="email"
                value={posForm.email}
                onChange={(e) => setPosForm(prev => ({ ...prev, email: e.target.value }))}
                placeholder="Ex: pos@entreprise.com"
              />
            </FormField>
          </div>
          
          <div className="flex justify-end gap-3 pt-4">
            <Button
              variant="outline"
              onClick={() => {
                setShowPosModal(false);
                setEditingPos(null);
                setPosForm({ name: '', branchId: '', address: '', phone: '', email: '', posType: 'shop', manager: '' });
              }}
            >
              Annuler
            </Button>
            <Button
              variant="primary"
              onClick={handleCreatePos}
              disabled={formLoading}
            >
              {formLoading ? (
                <>
                  <Loader className="h-4 w-4 animate-spin mr-2" />
                  {editingPos ? 'Mise à jour...' : 'Création...'}
                </>
              ) : editingPos ? 'Modifier' : 'Créer'}
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
}